import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, RefreshCw, Eye } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { stockTransferService } from '../../services/stockTransferService';
import { StockTransferListDTO, StockTransferStatus } from '../../types';
import './StockTransferPage.css';

const STATUS_LABEL: Partial<Record<StockTransferStatus, string>> = {
  TransferredOut:    'Transferred Out',
  PartiallyReceived: 'Partially Received',
};

const STATUS_CLASS: Partial<Record<StockTransferStatus, string>> = {
  TransferredOut:    'ims-badge st-badge-transferredout',
  PartiallyReceived: 'ims-badge st-badge-partiallyreceived',
};

const StockTransferInPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t }     = useTranslation();

  const [transfers, setTransfers] = useState<StockTransferListDTO[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(
    (location.state as any)?.message || null
  );

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!successMsg) return;
    const timer = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(timer);
  }, [successMsg]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await stockTransferService.getAll(1, 200, 'TransferredOut,PartiallyReceived');
      setTransfers(res.data?.data?.data ?? []);
    } catch {
      setError(t('stockTransfer.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColumn[] = [
    { key: 'transferNumber',          label: t('stockTransfer.transferNumber'), sortable: true, width: '160px' },
    {
      key: 'transferDate',
      label: t('stockTransfer.transferDate'),
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
    { key: 'sourceWarehouseName',      label: t('stockTransfer.source'),      width: '160px' },
    { key: 'destinationWarehouseName', label: t('stockTransfer.destination'),  width: '160px' },
    {
      key: 'expectedArrivalDate',
      label: t('stockTransfer.expectedArrival'),
      width: '130px',
      render: (v) => v ? new Date(v as string).toLocaleDateString() : '—',
    },
    {
      key: 'status',
      label: t('common.status'),
      width: '160px',
      render: (v) => {
        const s = v as StockTransferStatus;
        return <span className={STATUS_CLASS[s] || 'ims-badge'}>{STATUS_LABEL[s] ?? s}</span>;
      },
    },
    { key: 'itemCount',       label: t('stockTransfer.items'),       width: '80px' },
    { key: 'requestedByName', label: t('stockTransfer.requestedBy'), width: '140px' },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      title: t('stockTransfer.receive'),
      onClick: (row) => navigate(`/stock-transfers/${row.id}`),
      className: 'grid-action-btn-view',
    },
  ];

  return (
    <Layout>
      <div className="st-page">
        <div className="ims-list-header">
          <div className="ims-list-header-left">
            <h1 className="ims-list-title">
              <Download size={22} strokeWidth={1.75} style={{ marginRight: 8 }} />
              {t('stockTransfer.transferIn')}
            </h1>
            <p className="ims-list-subtitle">{t('stockTransfer.transferInPageDesc')}</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={loadData} disabled={loading}>
              {loading ? <RefreshCw size={14} strokeWidth={2} /> : t('common.refresh')}
            </button>
          </div>
        </div>

        <div className="st-pending-banner">
          <Download size={16} strokeWidth={2} />
          {t('stockTransfer.transferInBanner')}
        </div>

        {successMsg && <div className="ims-alert ims-alert-success">{successMsg}</div>}
        {error      && <div className="ims-alert ims-alert-error">{error}</div>}

        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={transfers}
            loading={loading}
            actions={actions}
            emptyMessage={t('stockTransfer.noInTransitTransfers')}
          />
        </div>
      </div>
    </Layout>
  );
};

export default StockTransferInPage;
